import { Flex, Icon, IconButton, Text, VStack } from '@chakra-ui/react'
import { Settings } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrackedExtension, TrackedParkingLot } from '~/atoms/WebphoneState'
import { GroupedScrollingList } from '~/components/shared/GroupedScrollingList'
import { GroupedScrollingListHeader } from '~/components/shared/GroupedScrollingListGroupHeader'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { TabButton } from '~/components/shared/TabButton'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { SpeedDialState } from '~/types/WebphoneTypes'

import {
  QUICK_DIAL_GROUP_SETTINGS_CLASS_NAME,
  useQuickDialViewStyle,
} from './componentStyles/QuickDialView.style'
import {
  EmptyLineExtensionListItem,
  EmptyParkingLotListItem,
  EmptyRecentlyDialedListItem,
  EmptySpeedDialListItem,
  LineExtensionListItem,
  ListItemData,
  ListItemDataType,
  ParkingLotListItem,
  RecentlyDialedListItem,
  RecentlyDialedListItemData,
  SpeedDialListItem,
} from './QuickDialListItem'

interface ListItemProps {
  itemData: ListItemData
  hasActiveCall: boolean
  showNewCallButton: boolean
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onPark: (targetNumber: string) => void
  onTransfer: (targetNumber: string) => void
  onNewCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onParkSettingsLinkClick: () => void
}
const ListItem = ({
  itemData,
  hasActiveCall,
  showNewCallButton,
  onStartCall,
  onPark,
  onTransfer,
  onNewCall,
  onParkSettingsLinkClick,
}: ListItemProps) => {
  if (itemData.dataType === ListItemDataType.EmptyParkingLot) {
    return (
      <EmptyParkingLotListItem onSettingsLinkClick={onParkSettingsLinkClick} />
    )
  }
  if (itemData.dataType === ListItemDataType.ParkingLot) {
    return (
      <ParkingLotListItem
        itemData={itemData}
        hasActiveCall={hasActiveCall}
        onStartCall={onStartCall}
        onTransfer={onPark}
        onNewCall={onNewCall}
      />
    )
  }
  if (itemData.dataType === ListItemDataType.EmptyExtension) {
    return (
      <EmptyLineExtensionListItem
        onSettingsLinkClick={onParkSettingsLinkClick}
      />
    )
  }
  if (itemData.dataType === ListItemDataType.Extension) {
    return (
      <LineExtensionListItem
        itemData={itemData}
        hasActiveCall={hasActiveCall}
        showNewCallButton={showNewCallButton}
        onStartCall={onStartCall}
        onTransfer={onTransfer}
        onNewCall={onNewCall}
      />
    )
  }
  if (itemData.dataType === ListItemDataType.Contact) {
    return (
      <RecentlyDialedListItem
        itemData={itemData}
        hasActiveCall={hasActiveCall}
        showNewCallButton={showNewCallButton}
        onStartCall={onStartCall}
        onTransfer={onTransfer}
        onNewCall={onNewCall}
      />
    )
  }
  if (itemData.dataType === ListItemDataType.SpeedDial) {
    return (
      <SpeedDialListItem
        itemData={itemData}
        hasActiveCall={hasActiveCall}
        showNewCallButton={showNewCallButton}
        onStartCall={onStartCall}
        onNewCall={onNewCall}
      />
    )
  }
  if (itemData.dataType === ListItemDataType.EmptySpeedDial) {
    return (
      <EmptySpeedDialListItem onSettingsLinkClick={onParkSettingsLinkClick} />
    )
  }
}

interface QuickDialViewProps {
  hasActiveCall: boolean
  isAtMaxCallLimit: boolean
  parkingLots: TrackedParkingLot[]
  extensions: TrackedExtension[]
  recentlyDialed: ContactDetails[]
  speedDial: SpeedDialState
  onPark: (targetNumber: string) => void
  onTransfer: (targetNumber: string) => void
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onNewCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  onParkSettingsLinkClick: () => void
}
export const QuickDialView = ({
  hasActiveCall,
  isAtMaxCallLimit,
  parkingLots,
  extensions,
  recentlyDialed,
  speedDial,
  onPark,
  onTransfer,
  onStartCall,
  onNewCall,
  onParkSettingsLinkClick,
}: QuickDialViewProps) => {
  const [activeTab, setActiveTab] = useState<'favorites' | 'recentlyDialed'>(
    'favorites',
  )
  const { t } = useTranslation()

  const presenceListData = useMemo(() => {
    const data = new Map<string, ListItemData[]>()

    const parkingLotData = parkingLots.map(
      (parkingLot) =>
        ({
          dataType: ListItemDataType.ParkingLot,
          id: parkingLot.id,
          label: parkingLot.label,
          targetNumber: parkingLot.targetNumber,
          presence: parkingLot.presence,
        }) as ListItemData,
    )
    if (parkingLotData.length === 0)
      parkingLotData.push({ dataType: ListItemDataType.EmptyParkingLot, id: 0 })
    data.set(t('quickDialView.groupName.parkingSpots'), parkingLotData)

    const extensionData = extensions.map(
      (extension) =>
        ({
          dataType: ListItemDataType.Extension,
          id: extension.id,
          name: extension.name,
          number: extension.number,
          presence: extension.presence,
        }) as ListItemData,
    )
    if (extensionData.length === 0)
      extensionData.push({ dataType: ListItemDataType.EmptyExtension, id: 0 })
    data.set(t('quickDialView.groupName.lineExtensions'), extensionData)

    const speedDialData = Array.from(speedDial).map(
      ([key, value]) =>
        ({
          dataType: ListItemDataType.SpeedDial,
          id: Number(key),
          label: value.label,
          number: value.number,
        }) as ListItemData,
    )
    if (speedDialData.length === 0)
      speedDialData.push({ dataType: ListItemDataType.EmptySpeedDial, id: 0 })
    data.set(t('quickDialView.groupName.speedDial'), speedDialData)
    return data
  }, [parkingLots, extensions])

  const recentlyDialedListData = useMemo(() => {
    return recentlyDialed.map(
      (contact) =>
        ({
          dataType: 'contact',
          contact,
          id: contact.id,
        }) as RecentlyDialedListItemData,
    )
  }, [recentlyDialed])

  const styles = useQuickDialViewStyle()

  const listAreaContent = useMemo(() => {
    if (activeTab === 'favorites') {
      return (
        <GroupedScrollingList
          css={styles.scrollingList}
          itemSpacing="0.25rem"
          groupItemData={presenceListData}
          onBuildGroupContent={(index: number, data: string) => {
            const marginTop = index === 0 ? '0' : '0.5rem'
            return (
              <GroupedScrollingListHeader
                index={index}
                mt={marginTop}
                mb="0.5rem"
                css={styles.groupHeader}
              >
                <Flex css={styles.header}>
                  <Text css={styles.headerText}>{data}</Text>
                  <IconButton
                    aria-label={t('quickDialView.phoneSettingsFor', {
                      defaultValue: 'Phone settings: {{group}}',
                      group: data,
                    })}
                    className={QUICK_DIAL_GROUP_SETTINGS_CLASS_NAME}
                    onClick={onParkSettingsLinkClick}
                    css={styles.headerButton}
                  >
                    <Icon css={styles.headerIcon}>
                      <Settings size="100%" />
                    </Icon>
                  </IconButton>
                </Flex>
              </GroupedScrollingListHeader>
            )
          }}
          onBuildItemContent={(index: number, itemData: ListItemData) => {
            return (
              <ListItem
                key={`${index}:${itemData.id}`}
                itemData={itemData}
                hasActiveCall={hasActiveCall}
                showNewCallButton={!isAtMaxCallLimit}
                onNewCall={onNewCall}
                onPark={onPark}
                onTransfer={onTransfer}
                onStartCall={onStartCall}
                onParkSettingsLinkClick={onParkSettingsLinkClick}
              />
            )
          }}
          showLoaderFooter={false}
        />
      )
    }
    if (activeTab === 'recentlyDialed') {
      if (recentlyDialedListData.length === 0) {
        return <EmptyRecentlyDialedListItem />
      }

      return (
        <ScrollingList
          css={styles.scrollingList}
          itemSpacing="0.25rem"
          showLoaderFooter={false}
          scrollbarOptions={{ showOnHoverOnly: true }}
          itemData={recentlyDialedListData}
          onBuildItemContent={(_, itemData) => {
            return (
              <ListItem
                key={`${itemData.id}:${itemData.contact.defaultNumber?.number}`}
                itemData={itemData}
                hasActiveCall={hasActiveCall}
                showNewCallButton={!isAtMaxCallLimit}
                onNewCall={onNewCall}
                onPark={onPark}
                onTransfer={onTransfer}
                onStartCall={onStartCall}
                onParkSettingsLinkClick={onParkSettingsLinkClick}
              />
            )
          }}
        />
      )
    }
  }, [
    activeTab,
    presenceListData,
    recentlyDialedListData,
    hasActiveCall,
    isAtMaxCallLimit,
    onTransfer,
  ])

  // Extract layout properties from tabContainer styles (Chakra v3 requirement)
  const tabContainerLayoutProps = {
    alignItems: styles.tabContainer?.alignItems,
    gap: styles.tabContainer?.gap,
  }

  return (
    <VStack h="full" w="full" css={styles.stack}>
      <Flex w="full" css={styles.tabContainer} {...tabContainerLayoutProps}>
        <TabButton
          text={t('quickDialView.tab.favorites')}
          isActive={activeTab === 'favorites'}
          onClick={() => setActiveTab('favorites')}
        />
        <TabButton
          text={t('quickDialView.tab.recentlyDialed')}
          isActive={activeTab === 'recentlyDialed'}
          onClick={() => setActiveTab('recentlyDialed')}
        />
      </Flex>
      {listAreaContent}
    </VStack>
  )
}
